import { useState, useRef, useCallback } from "react";
import { useAuth, useUser } from "@clerk/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { TopBar } from "../components/TopBar.js";
import { adminCatalogImport } from "../api.js";
import styles from "../styles/pages/AdminCatalogImportPage.module.css";

interface ParsedFile {
  filename: string;
  content: string;
  feedCount: number;
  categories: string[];
}

// Lightweight client-side OPML feed counter for the preview
function countFeeds(xml: string): { count: number; categories: string[] } {
  const categorySet = new Set<string>();
  const categoryStack: string[] = [];
  let count = 0;
  const re = /(<\/outline\s*>)|<outline\b([^>]*)(\/?)>/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(xml)) !== null) {
    if (m[1]) { categoryStack.pop(); continue; }
    const attrs = m[2] ?? "";
    const selfClosing = m[3] === "/" || attrs.trimEnd().endsWith("/");
    const getAttr = (name: string) => attrs.match(new RegExp(`\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)')`, "i"))?.[1];
    const type = getAttr("type")?.toLowerCase();
    const xmlUrl = getAttr("xmlUrl");
    if (type === "rss" && xmlUrl) {
      count++;
      categorySet.add(categoryStack.at(-1) ?? "Uncategorised");
    }
    if (!selfClosing) {
      const text = getAttr("text") ?? "";
      categoryStack.push(text || "Uncategorised");
    }
  }
  return { count, categories: Array.from(categorySet).sort() };
}

export function AdminCatalogImportPage() {
  const { isLoaded, userId } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  const role = (user?.publicMetadata as { role?: string } | undefined)?.role;
  const isAdmin = role === "admin" || role === "editor";
  const fileRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<ParsedFile[]>([]);
  const [dragging, setDragging] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<{ inserted: number; skipped: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (isLoaded && (!userId || !isAdmin)) {
    navigate({ to: "/" });
    return null;
  }

  async function readFiles(fileList: FileList) {
    const parsed: ParsedFile[] = [];
    for (const file of Array.from(fileList)) {
      const content = await file.text();
      const { count, categories } = countFeeds(content);
      parsed.push({ filename: file.name, content, feedCount: count, categories });
    }
    setFiles((prev) => {
      const existing = new Set(prev.map((f) => f.filename));
      return [...prev, ...parsed.filter((f) => !existing.has(f.filename))];
    });
    setResult(null);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files?.length) readFiles(e.target.files);
    e.target.value = "";
  }

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.length) readFiles(e.dataTransfer.files);
  }, []);

  async function handleImport() {
    if (!files.length) return;
    setImporting(true);
    setError(null);
    try {
      const res = await adminCatalogImport(files.map((f) => ({ filename: f.filename, content: f.content })));
      setResult({ inserted: res.inserted, skipped: res.skipped });
      setFiles([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import failed");
    } finally {
      setImporting(false);
    }
  }

  const totalFeeds = files.reduce((n, f) => n + f.feedCount, 0);

  return (
    <div className={styles.page}>
      <TopBar />
      <div className={styles.content}>
        <div className={styles.header}>
          <Link to="/admin" className={styles.back}>← Admin</Link>
          <h1 className={styles.title}>Import OPML</h1>
          {result && (
            <Link to="/admin/catalog/review" className={styles.reviewLink}>
              Review queue →
            </Link>
          )}
        </div>

        {result ? (
          <div className={styles.resultCard}>
            <p className={styles.resultLine}><strong>{result.inserted}</strong> sources added to review queue.</p>
            {result.skipped > 0 && (
              <p className={styles.resultSkipped}>{result.skipped} skipped (already in catalog).</p>
            )}
            <div className={styles.resultActions}>
              <button type="button" className={styles.primaryBtn} onClick={() => { setResult(null); }}>
                Import more
              </button>
              <Link to="/admin/catalog/review" className={styles.secondaryBtn}>
                Go to review queue →
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div
              className={`${styles.dropzone} ${dragging ? styles.dropzoneDragging : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
            >
              <input
                ref={fileRef}
                type="file"
                accept=".opml,.xml"
                multiple
                hidden
                onChange={handleFileChange}
              />
              <p className={styles.dropzoneLabel}>Drop .opml / .xml files here, or click to browse</p>
            </div>

            {files.length > 0 && (
              <>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>File</th>
                      <th>Feeds</th>
                      <th>Categories</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {files.map((f) => (
                      <tr key={f.filename}>
                        <td className={styles.tdFilename}>{f.filename}</td>
                        <td className={styles.tdCount}>{f.feedCount}</td>
                        <td className={styles.tdCategories}>{f.categories.join(", ") || "—"}</td>
                        <td>
                          <button
                            type="button"
                            className={styles.removeBtn}
                            onClick={() => setFiles((prev) => prev.filter((x) => x.filename !== f.filename))}
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                    <tr className={styles.totalRow}>
                      <td>Total</td>
                      <td className={styles.tdCount}>{totalFeeds}</td>
                      <td></td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>

                {error && <p className={styles.error}>{error}</p>}

                <div className={styles.actions}>
                  <button type="button" className={styles.primaryBtn} onClick={handleImport} disabled={importing}>
                    {importing ? "Importing…" : `Import ${totalFeeds} feeds`}
                  </button>
                  <button type="button" className={styles.ghostBtn} onClick={() => setFiles([])}>
                    Clear
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
